import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link2, MessageSquare, QrCode, Copy, Check } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { BottomSheet } from "./BottomSheet";
import { useAuth } from "../context/AuthContext";

export function InviteSheet({ open, onClose }) {
  const { family, user } = useAuth();
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  const code = family?.invite_code || "";
  const link = `${window.location.origin}/join/${code}`;
  const message = `Hey, I just joined our family's vault! Join the vault to stay connected!\n\nHere's my personal referral link: ${link}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — link: " + link);
    }
  };

  const textMessage = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(message)}`;
  };

  const Tile = ({ icon: Icon, label, onClick, testid, active }) => (
    <motion.button whileTap={{ scale: 0.95 }} onClick={onClick} data-testid={testid}
      className="flex flex-col items-center justify-center gap-2 rounded-2xl py-5 card-surface"
      style={active ? { border: "1px solid color-mix(in srgb, var(--accent) 50%, transparent)" } : {}}>
      <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
        <Icon size={20} className="gold-text" />
      </div>
      <span className="text-xs font-semibold">{label}</span>
    </motion.button>
  );

  return (
    <BottomSheet open={open} onClose={() => { setShowQR(false); onClose(); }} title="Invite Family">
      <div className="grid grid-cols-3 gap-3">
        <Tile icon={copied ? Check : Copy} label="Copy link" onClick={copyLink} testid="invite-copy-link" />
        <Tile icon={MessageSquare} label="Text" onClick={textMessage} testid="invite-text-message" />
        <Tile icon={QrCode} label="QR code" onClick={() => setShowQR((s) => !s)} testid="invite-qr-toggle" active={showQR} />
      </div>

      {showQR && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mt-5 flex flex-col items-center" data-testid="invite-qr-panel">
          <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
            <QRCodeCanvas value={link} size={200} fgColor="#171826" bgColor="#ffffff" includeMargin={false} />
          </div>
          <p className="text-[11px] mt-3 text-center" style={{ color: "var(--text-muted)" }}>
            Scan with a phone camera to open the invite for {family?.name}.
          </p>
        </motion.div>
      )}

      <div className="mt-5 rounded-xl px-4 py-3 flex items-center gap-2" style={{ background: "var(--overlay)" }}>
        <Link2 size={14} className="gold-text" />
        <span className="text-xs" style={{ color: "var(--text-secondary)" }}>Invite code: </span>
        <span className="text-xs font-bold tracking-widest" style={{ color: "var(--text-primary)" }}>{code}</span>
      </div>
    </BottomSheet>
  );
}
